// SHARED FOUNDATION — owned by the page skeleton; FROZEN for tab agents.
// The tab strip of the customer page (mock s4) + panel host. Tab content
// comes exclusively from the registry (./tabs/registry.ts); the active tab is
// deep-linked via the `?tab=<id>` search param; visited tabs stay MOUNTED
// (hidden, not unmounted) so switching back is instant.
//
// THE STRIP IS ONE CONTROL AT EVERY WIDTH: lab TabContext + TabList
// variant="scrollable", copied from the proven ListingDetailsDialog pattern,
// with a local sx override that tightens the theme's spacing(5) inter-tab gap
// (scroll buttons are replaced with an RTL-safe implementation — see
// RtlSafeTabScrollButton).
//
// It used to fork below sm into a row of chip BUTTONS with floating corner
// badges. That fork is gone (design feedback 2026-08-17: "change the contact
// page tabs to be more similar to scrollable tabs instead of buttons"), and
// deleting it removed more than a look: the chip strip carried its own
// scroll-into-view effect, its own badge shape, its own hitbox-extender
// pseudo-element and its own active-state styling, all of which the TabList
// already does natively. Narrow widths now differ from wide ones only in
// TYPE SCALE and PADDING (see `stripSx`), not in structure.
//
// Tab visibility is PER CONTACT: registry defs may carry an isVisible(contact)
// predicate (e.g. matches hides for pure seller-side contacts); the visible
// list is resolved once per contact here and drives the strip, the panels,
// and the ?tab= fallback alike.
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tab, Box, Stack, TabScrollButton, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { TabContext, TabList } from "@material-ui/lab";
import { useDragToScroll } from "hooks/useDragToScroll";
import Label from "components/Label";
import {
  getVisibleContactDetailTabs,
  isContactDetailTabId
} from "./tabs/registry";
import type { ContactDetailTabId, ContactDetailTabDefinition } from "./tabs/registry";
import type { ForwardRefExoticComponent, RefAttributes } from "react";
import type { TabScrollButtonProps, TabsActions } from "@material-ui/core";
import type { RealEstateContact } from "../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabs/tabContract";

export const CONTACT_DETAIL_TAB_PARAM = "tab";
const DEFAULT_TAB: ContactDetailTabId = "overview";

// Mobile strip ordering: the less-worked tabs are pushed to the END of the
// scrollable row so the first screenful holds the tabs a realtor actually
// lives in. Desktop keeps the registry order.
const MOBILE_LAST_TABS: ContactDetailTabId[] = ["timeline", "calls", "signatures"];

type ContactDetailTabsProps = {
  contact: RealEstateContact;
  /**
   * Render the NARROW form regardless of the viewport.
   *
   * For hosts that are themselves narrow on a wide screen — today the CRM side panel,
   * which is a 420–460px column beside the list. Without this the strip and the panels
   * read `useMediaQuery` against the VIEWPORT and lay themselves out for the 1440px
   * screen they are technically on, inside a 420px box. Same class of bug as the CRM
   * row's `roomy` breakpoint, and the same fix: the container decides, not the window.
   */
  compact?: boolean;
  /**
   * Layout override: receives the tab STRIP and the PANELS as two separate elements so
   * the host can place the strip inside its own sticky header block while the panels
   * scroll (the on-page layout, design feedback 2026-08).
   *
   * ⚠️ IT USED TO BE DESKTOP-ONLY, and widening it is what makes a narrow host able to
   * pin its tabs. The CRM side panel needs exactly the same split — its header is sticky
   * and the strip scrolled away underneath it, reported as "keep the top tabs fixed,
   * along with the header" — but it renders `compact`, so the old `!isNarrow` gate meant
   * the one host that needed the hook most was the one host that could not use it.
   *
   * What the gate ALSO did, and what is preserved: it decided the panels' padding. That
   * decision still keys on `isNarrow` alone (flat and full-bleed when narrow, floating
   * TabCards when wide), so a wide host with this hook is byte-identical to before and a
   * narrow host keeps the mobile treatment it should have had all along.
   *
   * Both elements must stay INSIDE the returned tree — they share this component's
   * `TabContext`.
   */
  renderLayout?: (strip: JSX.Element, panels: JSX.Element) => JSX.Element;
};

// Memoized panel body: visited tabs stay MOUNTED (hidden), and without memo
// every render of the strip — every ?tab= flip, every URL-param write, every
// badge-hook update — would re-reconcile the FULL tree of every visited tab.
// `Component` identities come from the static registry and `contact` identity
// only changes on real doc updates (useLiveContact), so memo is effective.
/**
 * MUI scrolls `.MuiTabs-scroller`, not the `Tabs` root — attaching the drag to the root
 * and setting ITS scrollLeft silently does nothing. Module scope so the identity is
 * stable: `useDragToScroll` memoizes on this, and an inline arrow would rebuild the
 * pointer handler on every render.
 */
const resolveTabsScroller = (host: HTMLElement): HTMLElement | null => host.querySelector(".MuiTabs-scroller");

const TabPanelBody = memo(function TabPanelBody({
  Component,
  contact
}: {
  Component: (props: ContactTabComponentProps) => JSX.Element;
  contact: RealEstateContact;
}): JSX.Element {
  return <Component contact={contact} />;
});

// Tiny wrapper so each tab's badge HOOK is called at a component top level
// (fixed hook order per instance) rather than inside a render loop.
//
// ONE badge shape at every width now that the chip strip is gone: an inline
// Label following the tab's own text. The narrow variant only shrinks it, so
// a 2-digit count still fits beside the label without forcing the tab wider.
function TabBadgeContent({
  contact,
  useBadge,
  dense
}: {
  contact: RealEstateContact;
  useBadge: (contact: RealEstateContact) => TabBadge;
  dense?: boolean;
}): JSX.Element | null {
  const badge = useBadge(contact);
  if (!badge || badge.count === undefined || badge.count === 0) return null;
  return (
    <Label
      color={badge.color ?? "default"}
      sx={dense ? { height: 18, minWidth: 18, px: 0.5, fontSize: 11, flexShrink: 0 } : { flexShrink: 0 }}
    >
      {badge.count}
    </Label>
  );
}

// HARD INVARIANT — per-tab KEYED badge instances. Badge hooks differ in their
// INTERNAL hook signatures (some call no hooks at all, others call
// useState/useEffect/context hooks), so React must never reuse one badge
// component instance across two different tabs' hooks. The old mobile
// <Select> made that reuse real (its value display re-rendered the SELECTED
// item's children IN PLACE, swapping `useBadge` inside one component instance
// and crashing with React's "Should have a queue" hooks-order error). The
// Select is gone, but the rule stands for ANY strip structure: every tab
// renders its OWN badge instance keyed by tab id — never render a badge hook
// for "the currently selected tab" in a shared slot.
function TabBadgeLabel({
  tabId,
  contact,
  useBadge,
  dense
}: {
  tabId: ContactDetailTabId;
  contact: RealEstateContact;
  useBadge: (contact: RealEstateContact) => TabBadge;
  dense?: boolean;
}): JSX.Element {
  return <TabBadgeContent key={tabId} contact={contact} useBadge={useBadge} dense={dense} />;
}

// ---------------------------------------------------------------------------
// RTL-safe scroll buttons for the >=sm TabList.
//
// The v5-beta Tabs scroll-button math is broken under direction:rtl: it mixes
// raw scrollLeft with values normalized via detectScrollType() /
// getNormalizedScrollLeft(), and the sign convention of RTL scrollLeft varies
// per browser (evergreen browsers report it in [-(scrollWidth-clientWidth), 0]
// — the CSSOM "negative" convention). When the beta's normalization guesses
// wrong, the computed scroll target clamps to the current position and the
// click visibly does nothing (observed: the physical-left chevron on tablet).
// Rather than patching the beta's arithmetic we bypass it entirely: this
// replacement ScrollButtonComponent ignores MUI's onClick/disabled and
//   1. scrolls with a PHYSICAL delta (scrollBy), which is sign-consistent in
//      every evergreen browser regardless of RTL normalization, and
//   2. derives its own disabled state from the physical scroll position
//      (disabled renders opacity-0, exactly like the stock button).
// The `direction` prop MUI passes IS the chevron's physical pointing side
// ('left' | 'right'), so mapping click → physical delta needs no RTL math.
// MUI still owns WHEN buttons render (scrollButtons="auto" overflow check),
// which is safe under any convention — only the click/disabled math is not.
//
// Why fix the TabList instead of extending the mobile chip grid to tablet:
// the TabList can overflow at ANY width >= sm (narrow tablet, squeezed
// desktop content column, browser zoom, future tabs), so the scroll buttons
// must work under RTL regardless — fixing them once covers every breakpoint.
// ---------------------------------------------------------------------------

// The beta's TabScrollButton d.ts is a plain function type, but the runtime
// component forwards refs (we need the DOM node to reach the sibling scroller).
const TabScrollButtonWithRef = TabScrollButton as unknown as ForwardRefExoticComponent<
  TabScrollButtonProps & RefAttributes<HTMLDivElement>
>;

// The scroll button is a direct sibling of the scroller inside the Tabs root.
function findTabsScroller(button: HTMLElement | null): HTMLElement | null {
  return button?.parentElement?.querySelector<HTMLElement>(".MuiTabs-scroller") ?? null;
}

// Physical distance (px) scrolled from the LEFT edge, convention-normalized:
// evergreen browsers report RTL scrollLeft in [-(max), 0], LTR in [0, max].
function getPhysicalScrollLeft(scroller: HTMLElement): number {
  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  const isRtlScroller = getComputedStyle(scroller).direction === "rtl";
  const fromLeft = isRtlScroller
    ? maxScroll + Math.min(scroller.scrollLeft, 0)
    : scroller.scrollLeft;
  return Math.max(0, Math.min(fromLeft, maxScroll));
}

function RtlSafeTabScrollButton(props: TabScrollButtonProps): JSX.Element {
  const { direction } = props;
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [disabled, setDisabled] = useState(false);

  // Track the scroller's physical position to keep this chevron's disabled
  // state truthful (scroll events also fire during smooth scrolling, so the
  // state settles as the animation lands; resize re-checks overflow).
  useEffect(() => {
    const scroller = findTabsScroller(buttonRef.current);
    if (!scroller) return undefined;
    const update = (): void => {
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const fromLeft = getPhysicalScrollLeft(scroller);
      // 1px slack absorbs subpixel rounding under browser zoom.
      setDisabled(direction === "left" ? fromLeft <= 1 : fromLeft >= maxScroll - 1);
    };
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [direction]);

  const handleScrollClick = (): void => {
    const scroller = findTabsScroller(buttonRef.current);
    if (!scroller) return;
    // ~90% of the visible strip per click; the browser clamps at the edges.
    const amount = scroller.clientWidth * 0.9;
    scroller.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
  };

  // Spread first, then override — MUI's onClick (broken math) and disabled
  // (broken normalization) must lose to ours.
  return (
    <TabScrollButtonWithRef
      {...props}
      ref={buttonRef}
      onClick={handleScrollClick}
      disabled={disabled}
    />
  );
}

export function ContactDetailTabs({
  contact,
  compact,
  renderLayout
}: ContactDetailTabsProps): JSX.Element {
  const theme = useTheme();
  // The hook is called UNCONDITIONALLY and combined afterwards — `compact || useMediaQuery(…)`
  // short-circuits, which would stop calling a hook the moment a host passed `compact`
  // and break the hook order on that render. `compact` is a HOST assertion ("I am narrow
  // whatever the window says") and wins over the viewport once both are known.
  const isSmallViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const isNarrow = Boolean(compact) || isSmallViewport;

  const [searchParams, setSearchParams] = useSearchParams();

  // Per-contact tab visibility, resolved ONCE per contact identity (the
  // registry predicates are pure functions of the contact doc). Every strip
  // variant, the panel host, and the active-tab fallback below all consume
  // THIS list so a hidden tab is consistently absent everywhere.
  const visibleTabs = useMemo(() => getVisibleContactDetailTabs(contact), [contact]);

  // NARROW strip order only — the panels and the wide strip keep the registry order.
  const mobileOrderedTabs = useMemo(
    () => [
      ...visibleTabs.filter((tab) => !MOBILE_LAST_TABS.includes(tab.id)),
      ...MOBILE_LAST_TABS.map((id) => visibleTabs.find((tab) => tab.id === id)).filter(
        (tab): tab is ContactDetailTabDefinition => !!tab
      )
    ],
    [visibleTabs]
  );

  const tabParam = searchParams.get(CONTACT_DETAIL_TAB_PARAM);
  // A ?tab= pointing at a tab this contact HIDES (e.g. ?tab=matches on a pure
  // seller) falls back to overview, same as an unknown value — the stale param
  // stays in the URL until the next tab change rewrites it (existing behavior
  // for invalid params).
  const activeTab: ContactDetailTabId =
    isContactDetailTabId(tabParam) && visibleTabs.some((tab) => tab.id === tabParam)
      ? tabParam
      : DEFAULT_TAB;

  const handleChangeTab = (nextTab: ContactDetailTabId): void => {
    if (nextTab === activeTab) return;
    // Merge (never rebuild) the search params — other URL-param mechanisms
    // (e.g. the global new-signature dialog) share the same search string.
    const next = new URLSearchParams(searchParams);
    if (nextTab === DEFAULT_TAB) next.delete(CONTACT_DETAIL_TAB_PARAM);
    else next.set(CONTACT_DETAIL_TAB_PARAM, nextTab);
    setSearchParams(next, { replace: true });
  };

  // Panels of tabs the user has visited stay mounted (hidden when inactive)
  // so flipping back doesn't refetch/re-render from scratch.
  const [visitedTabs, setVisitedTabs] = useState<ReadonlySet<ContactDetailTabId>>(
    () => new Set([activeTab])
  );
  useEffect(() => {
    setVisitedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  // ONE strip, two densities. Narrow hosts (a phone, or the CRM side panel on any
  // screen) only get smaller type, tighter padding and a lower minHeight — the
  // structure, the scroll behaviour, the RTL scroll buttons and the badge shape are
  // identical, so there is one thing to reason about instead of two.
  const tabStripSx = useMemo(
    () => ({
      px: isNarrow ? 0.5 : 2,
      borderBottom: 1,
      borderColor: "divider",
      minHeight: isNarrow ? 42 : undefined,
      // The theme spaces tabs 40px apart (fine for 3-4 page tabs, unusable for
      // 9). Tighten the gap + trim tab padding locally, exactly like
      // ListingDetailsDialog — never change the global Tabs override.
      "& .MuiTab-root:not(:last-of-type)": { marginRight: isNarrow ? 1.5 : 2 },
      "& .MuiTab-root": {
        px: 0.5,
        minWidth: "unset",
        ...(isNarrow ? { minHeight: 42, fontSize: 13, py: 0.5 } : null)
      },
      // The scroll buttons are 40px each and eat a seventh of a 420px panel. Narrow
      // hosts are touch- or trackpad-scrolled, so the strip keeps its own scrollbar
      // hidden and drops the chevrons rather than spending the width on them.
      ...(isNarrow
        ? {
            "& .MuiTabs-scroller": { scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }
          }
        : null)
    }),
    [isNarrow]
  );

  const stripTabs = isNarrow ? mobileOrderedTabs : visibleTabs;

  // Destructured, because the composed ref below closes over `attachDragToScroll` alone —
  // the hook's returned OBJECT is rebuilt every render while its two members are stable.
  const { ref: attachDragToScroll, onPointerDown: onStripPointerDown } =
    useDragToScroll(resolveTabsScroller);

  // ── THE UNDERLINE MUST FOLLOW A TAB THAT CHANGES WIDTH ─────────────────────────────
  //
  // Reported as "when picking the timeline tab, the underline is not under the timeline
  // text but way before" — in the side panel and on a phone, i.e. wherever the strip is
  // NARROW and therefore scrolls.
  //
  // MUI positions the indicator from a measurement it takes in an effect of ITS OWN
  // render (`updateIndicatorState` in Tabs.js), and nothing re-measures afterwards except
  // a window `resize`. Tab widths here are not fixed at mount: each tab's count badge is
  // fetched by its own hook (`TabBadgeLabel` → `useBadge`) and rendered inside the tab
  // when it lands, which widens that tab WITHOUT re-rendering `TabList`. Every tab after
  // it in the strip therefore slides along while the indicator stays at the coordinate it
  // was given, and the further down the strip a tab sits the bigger the error. In the
  // narrow ordering `MOBILE_LAST_TABS` pushes ציר זמן past every badge-carrying tab, which
  // is why that one is where it is unmissable. A font swap has the same shape, and MUI's
  // own source flags it ("May be wrong until the font is loaded").
  //
  // So: observe the tabs, and re-run MUI's own measurement whenever one changes size.
  // Cause-agnostic on purpose — a future badge, a longer label or a different font all
  // arrive through this one path. `action` is the documented API for exactly this; do NOT
  // reach for the indicator's inline style, which MUI overwrites on its next render.
  const tabsActionsRef = useRef<TabsActions | null>(null);
  const stripHostRef = useRef<HTMLElement | null>(null);

  // Composed, because `useDragToScroll` needs the same node — and its callback ref does
  // real work (it registers the capture-phase click swallower), so it cannot be dropped.
  const setStripHost = useCallback(
    (node: HTMLElement | null) => {
      stripHostRef.current = node;
      attachDragToScroll(node);
    },
    [attachDragToScroll]
  );

  useEffect(() => {
    const host = stripHostRef.current;
    if (!host || typeof ResizeObserver === "undefined") return undefined;
    const tabs = Array.from(host.querySelectorAll<HTMLElement>('[role="tab"]'));
    if (tabs.length === 0) return undefined;
    // The tabs are observed, never the indicator — `updateIndicator` writes only the
    // indicator's own style, so there is no observation here that can feed itself.
    const observer = new ResizeObserver(() => tabsActionsRef.current?.updateIndicator());
    tabs.forEach((tab) => observer.observe(tab));
    return () => observer.disconnect();
    // Re-attached when the strip's tab SET changes (a contact that hides `matches`, or a
    // host flipping between the narrow and wide orderings) — the old nodes are gone.
  }, [stripTabs]);

  const stripEl = (
    <TabList
      /* Grab-and-drag the strip with a mouse. `variant="scrollable"` gives a wheel, a
         trackpad and a touch swipe, but on a desktop there is nothing to grab and the
         only way past the overflow is the two chevrons — reported as "the tabs are not
         draggable". The scroller is an INNER element, hence the resolver. */
      ref={setStripHost}
      /* Hands the effect above MUI's own `updateIndicator()`, so the underline can be
         re-measured when a tab's width changes without `TabList` re-rendering. */
      action={tabsActionsRef}
      onPointerDown={onStripPointerDown}
      sx={{ ...tabStripSx, cursor: "grab", "&:active": { cursor: "grabbing" } }}
      value={activeTab}
      variant="scrollable"
      // Chevrons on wide hosts only; narrow ones scroll by touch/trackpad and
      // cannot spare 80px of strip for two buttons.
      scrollButtons={isNarrow ? false : "auto"}
      // RTL fix — the beta's built-in scroll-button math dead-clicks
      // under direction:rtl (see RtlSafeTabScrollButton above).
      ScrollButtonComponent={RtlSafeTabScrollButton}
      onChange={(_, newValue) => handleChangeTab(newValue as ContactDetailTabId)}
    >
      {stripTabs.map(({ id, label, useBadge }) => (
        <Tab
          key={id}
          value={id}
          label={
            <Stack direction="row" alignItems="center" gap={0.75}>
              <span>{label}</span>
              {/* Per-tab KEYED badge instance — the hooks-order invariant documented
                  above. Never render a badge hook for "the selected tab" in a shared
                  slot, whatever the strip looks like. */}
              <TabBadgeLabel tabId={id} contact={contact} useBadge={useBadge} dense={isNarrow} />
            </Stack>
          }
        />
      ))}
    </TabList>
  );

  /**
   * Two separate questions, and collapsing them is what broke the narrow host.
   *
   * `useHostLayout` — does the host want the strip and the panels handed to it separately?
   * `useFloatingCards` — do the panels float on the page with no grey surface? That one
   * is a WIDE-host treatment and stays keyed on the width, exactly as before.
   */
  const useHostLayout = !!renderLayout;
  const useFloatingCards = !isNarrow && useHostLayout;

  // Panels: visited AND visible. Filtering on visibleTabs (not just
  // visitedTabs) matters when the contact doc changes live — a visited
  // matches panel unmounts if the contact becomes a pure seller.
  const panelsEl = (
    <>
      {visibleTabs.filter(({ id }) => visitedTabs.has(id)).map(({ id, Component }) => (
        <Box
          key={id}
          role="tabpanel"
          hidden={id !== activeTab}
          sx={
            isNarrow
              ? // Narrow: flat, full-bleed content — no card chrome, no inset
                // (TabCard renders divider-separated padded Boxes here).
                { pt: 2, px: 0, pb: 2 }
              : useFloatingCards
              ? // On-page desktop layout: the TabCards float directly on the
                // page background — no grey surface, no horizontal inset.
                { pt: 2.5 }
              : // Legacy desktop surface (no layout override) — tab content
                // sits on a subtle grey area inside the hosting Card.
                {
                  p: 2.5,
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? theme.palette.grey[100]
                      : theme.palette.background.neutral
                }
          }
        >
          <TabPanelBody Component={Component} contact={contact} />
        </Box>
      ))}
    </>
  );

  return (
    <TabContext value={activeTab}>
      {useHostLayout ? (
        renderLayout!(stripEl, panelsEl)
      ) : (
        <>
          {stripEl}
          {panelsEl}
        </>
      )}
    </TabContext>
  );
}
